/**
 * CHART EXPORT CONFIGURATION - SINGLE SOURCE OF TRUTH
 * 
 * This configuration defines the baseline for chart rendering and export.
 * DO NOT MODIFY without explicit approval.
 * 
 * Baseline confirmed working: Native capture without dimension overrides
 * preserves SVG line/dot alignment correctly.
 * 
 * @version 1.0.0
 * @lastConfirmed 2024-02-26
 */

// Locked baseline configuration
export const CHART_EXPORT_CONFIG = {
  // html2canvas options - DO NOT add width/windowWidth overrides
  scale: 3,
  backgroundColor: '#ffffff',
  useCORS: true,
  logging: false,
  
  // PDF layout constraints
  maxChartHeightMm: 110,
  marginMm: 10,
  
  // Version for parity checking
  version: '1.0.0',
  configHash: 'baseline-native-capture-v1'
};

// Chart render configuration (must match export)
export const CHART_RENDER_CONFIG = {
  // Recharts ResponsiveContainer settings
  width: '100%',
  height: '100%',
  
  // Chart margins
  margin: { top: 10, right: 10, left: 0, bottom: 30 },
  
  // Grid settings
  gridStrokeDasharray: '3 3',
  gridStroke: '#e5e7eb',
  gridOpacity: 0.4,
  
  // Axis settings
  axisTickFill: '#6b7280',
  axisTickFontSize: 10,
  
  // Version for parity checking
  version: '1.0.0',
  configHash: 'baseline-native-capture-v1'
};

/**
 * Validates that render and export configs are in sync
 * Call this in development mode to catch regressions
 */
export const validateConfigParity = () => {
  const errors = [];
  
  // Check version match
  if (CHART_EXPORT_CONFIG.version !== CHART_RENDER_CONFIG.version) {
    errors.push(`Version mismatch: export=${CHART_EXPORT_CONFIG.version}, render=${CHART_RENDER_CONFIG.version}`);
  }
  
  // Check config hash match
  if (CHART_EXPORT_CONFIG.configHash !== CHART_RENDER_CONFIG.configHash) {
    errors.push(`Config hash mismatch: export=${CHART_EXPORT_CONFIG.configHash}, render=${CHART_RENDER_CONFIG.configHash}`);
  }
  
  // Verify no forbidden overrides in export config
  if (CHART_EXPORT_CONFIG.width !== undefined || CHART_EXPORT_CONFIG.windowWidth !== undefined) {
    errors.push('FORBIDDEN: width/windowWidth overrides detected in CHART_EXPORT_CONFIG - this breaks SVG alignment');
  }
  
  if (errors.length > 0) {
    console.error('CHART CONFIG PARITY CHECK FAILED:');
    errors.forEach(e => console.error(`  - ${e}`));
    
    if (process.env.NODE_ENV === 'development') {
      throw new Error('Chart config parity check failed. See console for details.');
    }
  }
  
  return errors.length === 0;
};

/**
 * Generates a parity report for all chart configurations
 */
export const generateParityReport = () => {
  const report = {
    timestamp: new Date().toISOString(),
    status: 'PASS',
    checks: []
  };
  
  // Check 1: Export config integrity
  const exportCheck = {
    name: 'Export Config Integrity',
    status: 'PASS',
    details: {}
  };
  
  if (CHART_EXPORT_CONFIG.width || CHART_EXPORT_CONFIG.windowWidth) {
    exportCheck.status = 'FAIL';
    exportCheck.details.error = 'Forbidden width overrides detected';
    report.status = 'FAIL';
  } else {
    exportCheck.details.scale = CHART_EXPORT_CONFIG.scale;
    exportCheck.details.configHash = CHART_EXPORT_CONFIG.configHash;
  }
  report.checks.push(exportCheck);
  
  // Check 2: Render config integrity  
  const renderCheck = {
    name: 'Render Config Integrity',
    status: 'PASS',
    details: {
      configHash: CHART_RENDER_CONFIG.configHash,
      margin: CHART_RENDER_CONFIG.margin
    }
  };
  report.checks.push(renderCheck);
  
  // Check 3: Version parity
  const versionCheck = {
    name: 'Version Parity',
    status: CHART_EXPORT_CONFIG.version === CHART_RENDER_CONFIG.version ? 'PASS' : 'FAIL',
    details: {
      exportVersion: CHART_EXPORT_CONFIG.version,
      renderVersion: CHART_RENDER_CONFIG.version
    }
  };
  if (versionCheck.status === 'FAIL') report.status = 'FAIL';
  report.checks.push(versionCheck);
  
  return report;
};

// Run parity check on module load in development
if (process.env.NODE_ENV === 'development') {
  validateConfigParity();
}

export default CHART_EXPORT_CONFIG;
